import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, Type } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import {
  ExternalPrefillConfirmation,
  ExternalPrefillProvider
} from '../specification-form/workflows/external-prefill-provider.model';

/**
 * The external-prefill block: prompt for an external id, confirm it against our API, then show a
 * linked banner and the workflow's own read-only card (design FR-10, FR-11, D4).
 *
 * **RENDERS NOTHING WITHOUT A PROVIDER**, which is every program today — the workflow registry
 * ships empty in production. That is the property that keeps FR-13 ("existing programs
 * behaviorally unchanged") true by construction rather than by testing, so the `*ngIf="provider"`
 * on the template root is load bearing, not defensive.
 *
 * This is a SHELL. It owns the prompt, the states and the failure copy; it owns no knowledge of any
 * particular integration. The VIDA ticket supplies a `prefillProvider` and a `blockComponent` and
 * this component hosts them unchanged.
 *
 * **The confirmation's `patch` is presentational.** It exists so the form can render immediately.
 * The SAVE carries only `token`, and the server re-derives the patch from its own stored record —
 * see `Project.externalPrefillToken`. Never send the patched values.
 */
@Component({
  selector: 'oes-external-prefill',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './external-prefill.component.html',
  styleUrls: ['./external-prefill.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExternalPrefillComponent {
  /** Undefined for every program without a workflow provider, which hides the whole block. */
  @Input() provider: ExternalPrefillProvider | null | undefined;
  /**
   * Program-scoped: eligibility, fixed-country precedence and authorization are all program
   * dependent, and "two programs share one workflow key" makes a key-only lookup ambiguous.
   */
  @Input() programId: string;
  /** The workflow's own read-only card, hosted via `ngComponentOutlet`. */
  @Input() blockComponent: Type<unknown> | null | undefined;
  @Input() disabled = false;

  /** Emitted once a link is confirmed. The parent applies the patch and keeps the token. */
  @Output() confirmed = new EventEmitter<ExternalPrefillConfirmation>();
  /** Emitted when a confirmed link is discarded, so the parent can drop the token. */
  @Output() cleared = new EventEmitter<void>();

  externalId = '';
  busy = false;
  confirmation: ExternalPrefillConfirmation | null = null;
  /**
   * Inputs for the workflow's block component (FR-14). Computed once per confirmation, never in
   * a getter: `ngComponentOutletInputs` shallow-compares each CD cycle, so a fresh object every
   * read would re-set the card's inputs on every pass.
   */
  blockInputs: Record<string, unknown> | null = null;
  /** i18n key for the current failure, or null. Never a raw provider string. */
  failureKey: string | null = null;
  /** Optional provider-supplied context, shown alongside the translated failure. */
  failureDetail: string | null = null;

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  get canConfirm(): boolean {
    return !!this.provider && !this.disabled && !this.busy && !!this.externalId?.trim();
  }

  async confirm(): Promise<void> {
    if (!this.canConfirm) {
      return;
    }
    this.busy = true;
    this.failureKey = null;
    this.failureDetail = null;

    try {
      const result = await this.provider.confirm(this.externalId.trim(), this.programId);
      if (result.outcome === 'CONFIRMED') {
        this.confirmation = result.confirmation;
        // The raw payload IS the card's data; the externalId rides along because VIDA's payload
        // does not repeat the slug.
        this.blockInputs = {
          application: {
            ...(typeof result.confirmation.raw === 'object' && result.confirmation.raw !== null
              ? result.confirmation.raw as Record<string, unknown> : {}),
            externalId: result.confirmation.externalId
          }
        };
        this.confirmed.emit(result.confirmation);
      } else {
        // A discriminated union rather than a thrown error, so each failure keeps its own copy:
        // the global HTTP error interceptor would flatten a throw into one generic message, and
        // ALREADY_CLAIMED in particular has to be distinguishable from NOT_FOUND to be actionable.
        this.failureKey = result.messageKey;
        this.failureDetail = result.detail ?? null;
      }
    } catch {
      // A provider that rejects instead of resolving is still our bug to absorb. The copy for
      // PROVIDER_ERROR must never read as the user's fault.
      this.failureKey = 'project.create.external-prefill.error.PROVIDER_ERROR';
    } finally {
      this.busy = false;
      // OnPush: `confirm()` resolves outside any template-driven event binding that would mark the
      // view dirty, so without this the banner never appears.
      this.changeDetectorRef.markForCheck();
    }
  }

  clear(): void {
    this.confirmation = null;
    this.blockInputs = null;
    this.failureKey = null;
    this.failureDetail = null;
    this.externalId = '';
    this.cleared.emit();
  }
}
